/**
 * Owns EEG trace/channel selection state for EEGViewer: which montage
 * channels are selected/locked/focused, per-channel gain + expanded-spacing
 * overrides, channel visibility (hide/isolate), the channel scroll viewport,
 * and the bulk-selection + per-channel-override action toolbar (mark bad,
 * apply/remove filter, temporary highlight, reset overrides) with its undo
 * stack.
 *
 * Deliberately NOT owned here (kept in EEGViewer and passed in as params)
 * even though they are conceptually "channel selection" state:
 *  - `hiddenChannels` / `setHiddenChannels` — EEGViewer's channel-color
 *    resolution pipeline (`resolveChannelColor`, `montageChannelDetails`,
 *    `buildDisplayProfileConfigFromCurrent`) reads this value synchronously
 *    while building the montage/channel list, well before this hook can be
 *    called (the hook also depends on `montageChannelDetails`, so it must be
 *    called *after* that computation exists). Moving `hiddenChannels` in here
 *    would create a genuine circular dependency, so EEGViewer keeps it.
 *  - `temporaryTraceHighlights` / `setTemporaryTraceHighlights` — same
 *    reasoning: it feeds `resolveChannelColor`/`resolveChannelOverrideColor`
 *    via a ref-mirror that EEGViewer seeds before `montageChannelDetails` is
 *    computed, i.e. before this hook could possibly be called.
 * Both are still fully wired through this hook's Phase-2 handlers (mark bad,
 * reset overrides, apply/clear highlight, undo) via params.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import type { QueryClient } from "@tanstack/react-query";
import apiClient from "@shared/services/apiClient";
import type { AxiosError } from "@shared/services/apiClient";
import type { useAppDialog } from "@shared/context/AppDialogContext";
import type EEGWaveformRenderer from "../services/EEGWaveformRenderer";
import type {
  BadChannelMark,
  FilterOverride,
} from "../components/customization/customizationTypes";
import {
  CHANNEL_REGION_OPTIONS,
  type ChannelRegionKey,
} from "../components/eegViewerChromeShared";
import { clampNumber } from "../components/eegViewerDisplayHelpers";
import { resetAllChannelOffsets } from "../utils/displayProfileVisualState";
import {
  TRACE_TEMP_HIGHLIGHT_DEFAULT,
  areSameLabelSelections,
  channelLabelMatchesRegion,
  dedupeLabelsInOrder,
  normalizeChannelPairKey,
  normalizeHexColor,
  type TraceSelectionUndoEntry,
} from "../types/eegViewerTraceTypes";

export type { TraceSelectionUndoEntry } from "../types/eegViewerTraceTypes";

/** Minimal shape of a montage channel row, as produced by EEGViewer's
 * `montageChannelDetails` memo. */
export interface EEGTraceChannelDetail {
  label: string;
  index: number;
  positive: string;
  negative: string;
  color: string;
}

/** Minimal slice of the decoded EEG buffer needed by
 * `estimateChannelAutoScaleGain`. Structurally compatible with EEGViewer's
 * private `DecodedArrowTile` type, so callers can pass that value directly
 * without a cast. */
export interface EEGTraceGainEstimateSource {
  channels: string[];
  columnArrays: Float32Array[];
  numSamples: number;
  sampling_rate: number;
}

/** Slice of the active EEG configuration needed for gain auto-scale. */
export interface EEGTraceConfigSnapshot {
  sensitivity_value?: number | null;
  time_scale?: number | null;
}

export interface ChannelGainMenuState {
  open: boolean;
  x: number;
  y: number;
  channelLabel: string | null;
}

export interface UseEEGTraceChannelSelectionParams {
  montageChannelDetails: EEGTraceChannelDetail[];
  visibleMontageChannelDetails: EEGTraceChannelDetail[];
  allMontageChannelLabels: string[];
  clinicalScopeId: string;
  queryClient: QueryClient;
  badChannelsQueryKey: readonly unknown[];
  filterOverridesQueryKey: readonly unknown[];
  refetchBadChannels: () => Promise<unknown>;
  refetchFilterOverrides: () => Promise<unknown>;
  badChannelRows: BadChannelMark[];
  filterOverrideRows: FilterOverride[];
  /** Canvas hit-testing only. */
  rendererRef: RefObject<EEGWaveformRenderer | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  /**
   * Ref-mirror of the EEG data buffer, kept in sync by EEGViewer. A plain
   * value can't be used here: EEGViewer's `eegData` query result isn't
   * declared until deep into the component (after this hook must already be
   * callable), so EEGViewer seeds this ref eagerly and assigns
   * `eegDataRef.current = eegData` once the query result exists.
   */
  eegDataRef: RefObject<EEGTraceGainEstimateSource | null>;
  /** Ref-mirror of the current playhead time (see `eegDataRef` for why). */
  currentTimeRef: RefObject<number>;
  dataWindowStart: number;
  currentConfig: EEGTraceConfigSnapshot | null;
  profileChannelPositionOffsets: Record<string, number>;
  profileChannelColors: Record<string, string>;
  setProfileChannelPositionOffsets: Dispatch<
    SetStateAction<Record<string, number>>
  >;
  setProfileChannelColors: Dispatch<SetStateAction<Record<string, string>>>;
  focusedSpacingSessionStorageKey: string | null;
  dialog: ReturnType<typeof useAppDialog>;
  hiddenChannels: Set<string>;
  setHiddenChannels: Dispatch<SetStateAction<Set<string>>>;
  temporaryTraceHighlights: Record<string, string>;
  setTemporaryTraceHighlights: Dispatch<SetStateAction<Record<string, string>>>;
  /** Shared with montage draft editing when provided by EEGViewer. */
  lastTraceSelectionRef?: React.MutableRefObject<string | null>;
}

function toUserFacingApiError(error: unknown): {
  status: number | null;
  message: string;
} {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  const status = axiosError?.response?.status ?? null;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    axiosError?.message ||
    "Request failed.";
  return { status, message };
}

function readJsonFromSessionStorage<T>(storageKey: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function useEEGTraceChannelSelection({
  montageChannelDetails,
  visibleMontageChannelDetails,
  allMontageChannelLabels,
  clinicalScopeId,
  queryClient,
  badChannelsQueryKey,
  filterOverridesQueryKey,
  refetchBadChannels,
  refetchFilterOverrides,
  badChannelRows,
  filterOverrideRows,
  rendererRef,
  canvasRef,
  eegDataRef,
  currentTimeRef,
  dataWindowStart,
  currentConfig,
  profileChannelPositionOffsets,
  profileChannelColors,
  setProfileChannelPositionOffsets,
  setProfileChannelColors,
  focusedSpacingSessionStorageKey,
  dialog,
  hiddenChannels,
  setHiddenChannels,
  temporaryTraceHighlights,
  setTemporaryTraceHighlights,
  lastTraceSelectionRef: externalLastTraceSelectionRef,
}: UseEEGTraceChannelSelectionParams) {
  const internalLastTraceSelectionRef = useRef<string | null>(null);
  const lastTraceSelectionRef =
    externalLastTraceSelectionRef ?? internalLastTraceSelectionRef;
  const [selectedTraceChannels, setSelectedTraceChannels] = useState<
    string[]
  >([]);
  const [lockedTraceFocusLabel, setLockedTraceFocusLabel] = useState<
    string | null
  >(null);
  const [channelRegionSelection, setChannelRegionSelection] =
    useState<ChannelRegionKey>("frontal");
  const [channelQuickJumpInput, setChannelQuickJumpInput] = useState("");
  const [traceFilterHp, setTraceFilterHp] = useState("");
  const [traceFilterLp, setTraceFilterLp] = useState("");
  const [traceFilterNotch, setTraceFilterNotch] = useState("");
  const [traceHighlightColor, setTraceHighlightColor] = useState(
    TRACE_TEMP_HIGHLIGHT_DEFAULT,
  );
  const [selectedTraceActionStatus, setSelectedTraceActionStatus] = useState<
    string | null
  >(null);
  const [selectedTraceActionError, setSelectedTraceActionError] = useState<
    string | null
  >(null);
  const [isApplyingSelectedTraceAction, setIsApplyingSelectedTraceAction] =
    useState(false);
  const [traceSelectionUndoStack, setTraceSelectionUndoStack] = useState<
    TraceSelectionUndoEntry[]
  >([]);
  const [channelScrollOffset, setChannelScrollOffset] = useState(0);
  const [visibleChannelCount, setVisibleChannelCount] = useState(20);
  const [channelGainOverrides, setChannelGainOverrides] = useState<
    Record<string, number>
  >({});
  const [channelGainMenu, setChannelGainMenu] = useState<ChannelGainMenuState>(
    {
      open: false,
      x: 0,
      y: 0,
      channelLabel: null,
    },
  );
  const [expandedSpacingChannelLabel, setExpandedSpacingChannelLabel] =
    useState<string | null>(null);

  const channelGainMenuRef = useRef<HTMLDivElement | null>(null);

  // Windowed subset of visible channels currently scrolled into view — used
  // only by handleSelectVisibleTraceChannels. Derived internally (rather than
  // accepted as a param) because it depends on channelScrollOffset /
  // visibleChannelCount, which this hook itself owns.
  const windowedVisibleMontageChannelLabels = useMemo(
    () =>
      visibleMontageChannelDetails
        .slice(
          channelScrollOffset,
          channelScrollOffset + Math.max(1, visibleChannelCount),
        )
        .map((channel) => channel.label),
    [channelScrollOffset, visibleMontageChannelDetails, visibleChannelCount],
  );

  // Local mirror of EEGViewer's `maxChannelScrollOffset` — needed by
  // handleQuickJumpToChannelLabel. Kept in sync by construction since both
  // are derived from the same visibleMontageChannelDetails/visibleChannelCount
  // inputs.
  const maxChannelScrollOffset = useMemo(
    () =>
      Math.max(
        0,
        visibleMontageChannelDetails.length - Math.max(1, visibleChannelCount),
      ),
    [visibleMontageChannelDetails.length, visibleChannelCount],
  );

  // Restore/persist the focused (expanded-spacing) channel per recording+montage.
  useEffect(() => {
    if (!focusedSpacingSessionStorageKey) {
      setExpandedSpacingChannelLabel(null);
      return;
    }

    const persisted = readJsonFromSessionStorage<unknown>(
      focusedSpacingSessionStorageKey,
      null,
    );
    setExpandedSpacingChannelLabel(
      typeof persisted === "string" && persisted.trim().length > 0
        ? persisted
        : null,
    );
  }, [focusedSpacingSessionStorageKey]);

  useEffect(() => {
    if (!focusedSpacingSessionStorageKey || typeof window === "undefined") {
      return;
    }

    try {
      if (expandedSpacingChannelLabel) {
        window.sessionStorage.setItem(
          focusedSpacingSessionStorageKey,
          JSON.stringify(expandedSpacingChannelLabel),
        );
      } else {
        window.sessionStorage.removeItem(focusedSpacingSessionStorageKey);
      }
    } catch {
      // Ignore persistence failures in constrained storage contexts.
    }
  }, [expandedSpacingChannelLabel, focusedSpacingSessionStorageKey]);

  useEffect(() => {
    if (!expandedSpacingChannelLabel) return;
    if (!allMontageChannelLabels.includes(expandedSpacingChannelLabel)) {
      setExpandedSpacingChannelLabel(null);
    }
  }, [allMontageChannelLabels, expandedSpacingChannelLabel]);

  // Drop selected/locked labels that no longer exist in the active montage.
  useEffect(() => {
    if (!montageChannelDetails.length) {
      setSelectedTraceChannels((current) => (current.length ? [] : current));
      lastTraceSelectionRef.current = null;
      setLockedTraceFocusLabel(null);
      return;
    }

    const validLabels = new Set(
      montageChannelDetails.map((item) => item.label),
    );
    setSelectedTraceChannels((current) => {
      const filtered = current.filter((label) => validLabels.has(label));
      if (
        filtered.length === current.length &&
        filtered.every((label, index) => label === current[index])
      ) {
        return current;
      }
      return filtered;
    });
    if (
      lastTraceSelectionRef.current &&
      !validLabels.has(lastTraceSelectionRef.current)
    ) {
      lastTraceSelectionRef.current = null;
    }
    setLockedTraceFocusLabel((current) =>
      current && !validLabels.has(current) ? null : current,
    );
  }, [montageChannelDetails]);

  const handleWaveformCanvasChannelSelection = useCallback(
    (event: MouseEvent) => {
      if (
        !rendererRef.current ||
        !canvasRef.current ||
        !visibleMontageChannelDetails.length
      ) {
        return;
      }

      if (event.ctrlKey) {
        return;
      }

      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const canvasX =
        ((event.clientX - rect.left) / rect.width) * canvasRef.current.width;
      const canvasY =
        ((event.clientY - rect.top) / rect.height) * canvasRef.current.height;
      const hit = rendererRef.current.getMontageChannelHitAtCanvasPoint(
        canvasX,
        canvasY,
      );
      if (!hit) return;

      const clickedLabel = hit.channel.label;
      if (lockedTraceFocusLabel) {
        setSelectedTraceChannels([lockedTraceFocusLabel]);
        lastTraceSelectionRef.current = lockedTraceFocusLabel;
        return;
      }
      const orderedLabels = visibleMontageChannelDetails.map(
        (channel) => channel.label,
      );
      const isToggleSelection = event.metaKey;

      setSelectedTraceChannels((current) => {
        if (
          event.shiftKey &&
          lastTraceSelectionRef.current &&
          orderedLabels.length > 0
        ) {
          const startIndex = orderedLabels.indexOf(
            lastTraceSelectionRef.current,
          );
          const endIndex = orderedLabels.indexOf(clickedLabel);
          if (startIndex >= 0 && endIndex >= 0) {
            const [from, to] =
              startIndex <= endIndex
                ? [startIndex, endIndex]
                : [endIndex, startIndex];
            const range = orderedLabels.slice(from, to + 1);
            if (isToggleSelection) {
              const merged = new Set(current);
              range.forEach((label) => merged.add(label));
              return Array.from(merged);
            }
            return range;
          }
        }

        if (isToggleSelection) {
          const next = new Set(current);
          if (next.has(clickedLabel)) {
            next.delete(clickedLabel);
          } else {
            next.add(clickedLabel);
          }
          return Array.from(next);
        }

        return [clickedLabel];
      });

      lastTraceSelectionRef.current = clickedLabel;
    },
    [canvasRef, lockedTraceFocusLabel, rendererRef, visibleMontageChannelDetails],
  );

  const selectedTraceActionTargets = useMemo(
    () => Array.from(new Set(selectedTraceChannels)),
    [selectedTraceChannels],
  );

  const estimateChannelAutoScaleGain = useCallback(
    (channelLabel: string): number => {
      const eegData = eegDataRef.current;
      if (!eegData?.columnArrays?.length || !eegData?.channels?.length) {
        return 1;
      }
      const detail = montageChannelDetails.find(
        (item) => item.label === channelLabel,
      );
      if (!detail) return 1;

      const samplingRate = eegData.sampling_rate || 256;
      const positiveIndex = eegData.channels.indexOf(detail.positive);
      if (positiveIndex < 0) return 1;
      const negativeIndex = detail.negative
        ? eegData.channels.indexOf(detail.negative)
        : -1;

      const totalSamples = eegData.numSamples;
      const currentTime = currentTimeRef.current ?? 0;
      const bufferRelativeStart = Math.max(0, currentTime - dataWindowStart);
      const startSample = clampNumber(
        Math.floor(bufferRelativeStart * samplingRate),
        0,
        Math.max(0, totalSamples - 1),
      );
      const windowSampleCount = clampNumber(
        Math.floor((currentConfig?.time_scale || 10) * samplingRate),
        Math.floor(samplingRate * 2),
        totalSamples - startSample,
      );
      const endSampleExclusive = Math.min(
        totalSamples,
        startSample + windowSampleCount,
      );

      const posCol = eegData.columnArrays[positiveIndex];
      const negCol =
        negativeIndex >= 0 ? eegData.columnArrays[negativeIndex] : null;
      let maxAbsUv = 0;
      for (let i = startSample; i < endSampleExclusive; i++) {
        const positiveUv = (posCol?.[i] ?? 0) * 1000000;
        const negativeUv = negCol ? (negCol[i] ?? 0) * 1000000 : 0;
        const differentialUv = positiveUv - negativeUv;
        const absValue = differentialUv < 0 ? -differentialUv : differentialUv;
        if (absValue > maxAbsUv) maxAbsUv = absValue;
      }

      if (!Number.isFinite(maxAbsUv) || maxAbsUv <= 0) return 1;
      const targetPeakUv = Math.max(
        20,
        (currentConfig?.sensitivity_value ?? 10) * 2.5,
      );
      return clampNumber(targetPeakUv / maxAbsUv, 0.1, 10);
    },
    [
      currentConfig?.sensitivity_value,
      currentConfig?.time_scale,
      currentTimeRef,
      dataWindowStart,
      eegDataRef,
      montageChannelDetails,
    ],
  );

  // EEG-CUX-002: channel visibility callbacks
  const hideChannel = useCallback(
    (label: string) => {
      setHiddenChannels((prev) => new Set([...prev, label]));
    },
    [setHiddenChannels],
  );

  const isolateChannel = useCallback(
    (label: string) => {
      const all = montageChannelDetails.map((ch) => ch.label);
      setHiddenChannels(new Set(all.filter((l) => l !== label)));
    },
    [montageChannelDetails, setHiddenChannels],
  );

  const showAllChannels = useCallback(() => {
    setHiddenChannels(new Set());
  }, [setHiddenChannels]);

  // EEG-CUX-003: isolate a set of channels (hide all others)
  const isolateChannels = useCallback(
    (labels: string[]) => {
      const labelSet = new Set(labels);
      const allLabels = montageChannelDetails.map((ch) => ch.label);
      setHiddenChannels(new Set(allLabels.filter((l) => !labelSet.has(l))));
    },
    [montageChannelDetails, setHiddenChannels],
  );

  const applyChannelGainOverride = useCallback(
    (channelLabel: string, gain: number) => {
      setChannelGainOverrides((current) => {
        const normalized = Number(gain);
        if (!Number.isFinite(normalized) || Math.abs(normalized - 1) < 0.0001) {
          if (!Object.prototype.hasOwnProperty.call(current, channelLabel))
            return current;
          const next = { ...current };
          delete next[channelLabel];
          return next;
        }
        if (current[channelLabel] === normalized) return current;
        return {
          ...current,
          [channelLabel]: normalized,
        };
      });
    },
    [],
  );

  const closeChannelGainMenu = useCallback(() => {
    setChannelGainMenu((current) =>
      current.open ? { ...current, open: false, channelLabel: null } : current,
    );
  }, []);

  const toggleExpandedSpacingForLabel = useCallback((channelLabel: string) => {
    setExpandedSpacingChannelLabel((current) =>
      current === channelLabel ? null : channelLabel,
    );
  }, []);

  const handleChannelGainPresetSelect = useCallback(
    (gain: number) => {
      const channelLabel = channelGainMenu.channelLabel;
      if (!channelLabel) return;
      applyChannelGainOverride(channelLabel, gain);
      closeChannelGainMenu();
    },
    [
      applyChannelGainOverride,
      channelGainMenu.channelLabel,
      closeChannelGainMenu,
    ],
  );

  const handleChannelAutoScaleSelect = useCallback(() => {
    const channelLabel = channelGainMenu.channelLabel;
    if (!channelLabel) return;
    applyChannelGainOverride(
      channelLabel,
      estimateChannelAutoScaleGain(channelLabel),
    );
    closeChannelGainMenu();
  }, [
    applyChannelGainOverride,
    channelGainMenu.channelLabel,
    closeChannelGainMenu,
    estimateChannelAutoScaleGain,
  ]);

  // EEG-CUX-001: channel visibility actions from the right-click context menu
  const handleChannelHideSelect = useCallback(() => {
    const label = channelGainMenu.channelLabel;
    if (label) {
      if (label === expandedSpacingChannelLabel) {
        setExpandedSpacingChannelLabel(null);
      }
      hideChannel(label);
    }
    closeChannelGainMenu();
  }, [
    channelGainMenu.channelLabel,
    closeChannelGainMenu,
    expandedSpacingChannelLabel,
    hideChannel,
  ]);

  const handleChannelIsolateSelect = useCallback(() => {
    const label = channelGainMenu.channelLabel;
    if (label) isolateChannel(label);
    closeChannelGainMenu();
  }, [channelGainMenu.channelLabel, isolateChannel, closeChannelGainMenu]);

  const handleChannelShowAllSelect = useCallback(() => {
    showAllChannels();
    closeChannelGainMenu();
  }, [showAllChannels, closeChannelGainMenu]);

  const handleChannelExpandedSpacingSelect = useCallback(() => {
    const label = channelGainMenu.channelLabel;
    if (!label) return;
    toggleExpandedSpacingForLabel(label);
    closeChannelGainMenu();
  }, [
    channelGainMenu.channelLabel,
    closeChannelGainMenu,
    toggleExpandedSpacingForLabel,
  ]);

  const handleResetExpandedSpacingSelect = useCallback(() => {
    setExpandedSpacingChannelLabel(null);
    closeChannelGainMenu();
  }, [closeChannelGainMenu]);

  const handleToggleTraceFocusLockSelect = useCallback(() => {
    const label = channelGainMenu.channelLabel;
    if (!label) return;
    setSelectedTraceChannels([label]);
    lastTraceSelectionRef.current = label;
    setLockedTraceFocusLabel((current) => (current === label ? null : label));
    closeChannelGainMenu();
  }, [channelGainMenu.channelLabel, closeChannelGainMenu]);

  const clearTraceFocusLock = useCallback(() => {
    setLockedTraceFocusLabel(null);
  }, []);

  const handleSelectedChannelSpacingToggle = useCallback(() => {
    const targetLabel = selectedTraceChannels[0];
    if (!targetLabel) return;
    toggleExpandedSpacingForLabel(targetLabel);
  }, [selectedTraceChannels, toggleExpandedSpacingForLabel]);

  const handleResetAllGainsSelect = useCallback(() => {
    setChannelGainOverrides({});
    closeChannelGainMenu();
  }, [closeChannelGainMenu]);

  const handleResetChannelAppearanceSelect = useCallback(() => {
    setSelectedTraceChannels([]);
    lastTraceSelectionRef.current = null;
    setLockedTraceFocusLabel(null);
    setExpandedSpacingChannelLabel(null);
    setChannelGainOverrides({});
    closeChannelGainMenu();
  }, [closeChannelGainMenu]);

  const handleResetChannelGainSelect = useCallback(() => {
    const label = channelGainMenu.channelLabel;
    if (label) applyChannelGainOverride(label, 1); // gain=1 removes override
    closeChannelGainMenu();
  }, [
    channelGainMenu.channelLabel,
    applyChannelGainOverride,
    closeChannelGainMenu,
  ]);

  useEffect(() => {
    if (!channelGainMenu.open) return;
    const onWindowPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (channelGainMenuRef.current && target) {
        if (channelGainMenuRef.current.contains(target)) {
          return;
        }
      }
      closeChannelGainMenu();
    };
    window.addEventListener("mousedown", onWindowPointerDown);
    return () => window.removeEventListener("mousedown", onWindowPointerDown);
  }, [channelGainMenu.open, closeChannelGainMenu]);

  const pushTraceSelectionUndoEntry = useCallback(
    (entry: TraceSelectionUndoEntry) => {
      setTraceSelectionUndoStack((current) => [...current, entry].slice(-25));
    },
    [],
  );

  const applyBulkTraceSelection = useCallback(
    (nextSelection: string[], label: string) => {
      const validLabelSet = new Set(allMontageChannelLabels);
      const normalizedNext = dedupeLabelsInOrder(nextSelection).filter(
        (channelLabel) => validLabelSet.has(channelLabel),
      );

      if (areSameLabelSelections(normalizedNext, selectedTraceChannels)) {
        setSelectedTraceActionError(null);
        setSelectedTraceActionStatus(`${label}.`);
        return;
      }

      pushTraceSelectionUndoEntry({
        kind: "selection_bulk",
        label,
        previousSelection: selectedTraceChannels,
      });
      setSelectedTraceChannels(normalizedNext);
      lastTraceSelectionRef.current =
        normalizedNext.length > 0
          ? normalizedNext[normalizedNext.length - 1]
          : null;
      setSelectedTraceActionError(null);
      setSelectedTraceActionStatus(
        `${label}: ${normalizedNext.length} selected.`,
      );
    },
    [allMontageChannelLabels, pushTraceSelectionUndoEntry, selectedTraceChannels],
  );

  const handleSelectAllTraceChannels = useCallback(() => {
    if (!allMontageChannelLabels.length) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No montage channels available.");
      return;
    }
    applyBulkTraceSelection(allMontageChannelLabels, "Selected all channels");
  }, [allMontageChannelLabels, applyBulkTraceSelection]);

  const handleClearAllTraceChannels = useCallback(() => {
    applyBulkTraceSelection([], "Cleared channel selection");
  }, [applyBulkTraceSelection]);

  const handleSelectVisibleTraceChannels = useCallback(() => {
    if (!windowedVisibleMontageChannelLabels.length) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No visible channels to select.");
      return;
    }
    applyBulkTraceSelection(
      windowedVisibleMontageChannelLabels,
      "Selected visible channels",
    );
  }, [applyBulkTraceSelection, windowedVisibleMontageChannelLabels]);

  const handleInvertTraceChannelSelection = useCallback(() => {
    if (!allMontageChannelLabels.length) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No montage channels available.");
      return;
    }
    const current = new Set(selectedTraceChannels);
    const inverted = allMontageChannelLabels.filter(
      (label) => !current.has(label),
    );
    applyBulkTraceSelection(inverted, "Inverted channel selection");
  }, [allMontageChannelLabels, applyBulkTraceSelection, selectedTraceChannels]);

  const handleSelectTraceChannelsByRegion = useCallback(() => {
    const matched = allMontageChannelLabels.filter((label) =>
      channelLabelMatchesRegion(label, channelRegionSelection),
    );
    if (!matched.length) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError(
        `No channels matched ${channelRegionSelection} region in current montage.`,
      );
      return;
    }
    const regionLabel =
      CHANNEL_REGION_OPTIONS.find(
        (option) => option.key === channelRegionSelection,
      )?.label || channelRegionSelection;
    applyBulkTraceSelection(matched, `Selected ${regionLabel} channels`);
  }, [allMontageChannelLabels, applyBulkTraceSelection, channelRegionSelection]);

  const handleQuickJumpToChannelLabel = useCallback(
    (rawQuery?: string) => {
      const query = (rawQuery ?? channelQuickJumpInput).trim().toLowerCase();
      if (!query) {
        setSelectedTraceActionStatus(null);
        setSelectedTraceActionError("Enter a channel label to jump.");
        return;
      }

      if (!allMontageChannelLabels.length) {
        setSelectedTraceActionStatus(null);
        setSelectedTraceActionError("No montage channels available.");
        return;
      }

      const normalizedLabels = allMontageChannelLabels.map((label) => ({
        label,
        normalized: label.toLowerCase(),
      }));

      const exactMatch = normalizedLabels.find(
        (item) => item.normalized === query,
      );
      const prefixMatch = normalizedLabels.find((item) =>
        item.normalized.startsWith(query),
      );
      const containsMatch = normalizedLabels.find((item) =>
        item.normalized.includes(query),
      );
      const targetLabel =
        exactMatch?.label ?? prefixMatch?.label ?? containsMatch?.label ?? null;

      if (!targetLabel) {
        setSelectedTraceActionStatus(null);
        setSelectedTraceActionError(`No channel matched "${query}".`);
        return;
      }

      const index = allMontageChannelLabels.indexOf(targetLabel);
      if (index >= 0) {
        const visibleStart = channelScrollOffset;
        const visibleEndExclusive =
          channelScrollOffset + Math.max(1, visibleChannelCount);
        if (index < visibleStart) {
          setChannelScrollOffset(clampNumber(index, 0, maxChannelScrollOffset));
        } else if (index >= visibleEndExclusive) {
          setChannelScrollOffset(
            clampNumber(
              index - Math.max(1, visibleChannelCount) + 1,
              0,
              maxChannelScrollOffset,
            ),
          );
        }
      }

      applyBulkTraceSelection([targetLabel], `Jumped to ${targetLabel}`);
      setChannelQuickJumpInput(targetLabel);
    },
    [
      allMontageChannelLabels,
      applyBulkTraceSelection,
      channelQuickJumpInput,
      channelScrollOffset,
      maxChannelScrollOffset,
      visibleChannelCount,
    ],
  );

  const handleResetAllPerChannelOverrides = useCallback(async () => {
    if (!allMontageChannelLabels.length) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No montage channels available.");
      return;
    }

    const normalizedTargetChannels = new Set(
      allMontageChannelLabels.map((channel) =>
        normalizeChannelPairKey(channel),
      ),
    );
    const previousFilterRows =
      queryClient.getQueryData<FilterOverride[]>(filterOverridesQueryKey) ||
      filterOverrideRows;
    const previousFilterByChannel: Record<string, FilterOverride | null> = {};
    allMontageChannelLabels.forEach((channelLabel) => {
      const normalized = normalizeChannelPairKey(channelLabel);
      previousFilterByChannel[channelLabel] =
        previousFilterRows.find(
          (row) => normalizeChannelPairKey(row.channel_name) === normalized,
        ) || null;
    });

    const hasFilterOverrides = Object.values(previousFilterByChannel).some(
      Boolean,
    );
    const hasGainOverrides = Object.keys(channelGainOverrides).length > 0;
    const hasHighlightOverrides =
      Object.keys(temporaryTraceHighlights).length > 0;
    const hasOffsetOverrides =
      Object.keys(profileChannelPositionOffsets).length > 0;
    const hasColorOverrides = Object.keys(profileChannelColors).length > 0;
    const hasAnyOverrides =
      hasFilterOverrides ||
      hasGainOverrides ||
      hasHighlightOverrides ||
      hasOffsetOverrides ||
      hasColorOverrides;

    if (!hasAnyOverrides) {
      setSelectedTraceActionError(null);
      setSelectedTraceActionStatus("No per-channel overrides to reset.");
      return;
    }

    if (
      !(await dialog.confirm(
        "Reset all per-channel overrides (gain/filter/color/offset/highlight) for this montage?",
      ))
    ) {
      return;
    }

    const previousChannelGainOverrides = { ...channelGainOverrides };
    const previousHighlightByChannel = { ...temporaryTraceHighlights };
    const previousProfileChannelPositionOffsets = {
      ...profileChannelPositionOffsets,
    };
    const previousProfileChannelColors = { ...profileChannelColors };

    setIsApplyingSelectedTraceAction(true);
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(null);

    const optimisticRows = previousFilterRows.filter((row) => {
      const normalized = normalizeChannelPairKey(row.channel_name);
      return !normalizedTargetChannels.has(normalized);
    });

    try {
      queryClient.setQueryData<FilterOverride[]>(
        filterOverridesQueryKey,
        optimisticRows,
      );

      setChannelGainOverrides({});
      setTemporaryTraceHighlights({});
      setProfileChannelPositionOffsets((current) =>
        resetAllChannelOffsets(current, allMontageChannelLabels),
      );
      setProfileChannelColors((current) =>
        Object.fromEntries(
          Object.entries(current).filter(
            ([channelLabel]) =>
              !normalizedTargetChannels.has(
                normalizeChannelPairKey(channelLabel),
              ),
          ),
        ),
      );

      if (hasFilterOverrides) {
        if (!clinicalScopeId) {
          throw new Error("No active recording selected.");
        }
        for (const channelLabel of allMontageChannelLabels) {
          const previous = previousFilterByChannel[channelLabel];
          if (!previous) continue;
          try {
            await apiClient.delete(
              `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channelLabel)}`,
            );
          } catch (error) {
            const normalizedError = toUserFacingApiError(error);
            if (normalizedError.status !== 404) {
              throw error;
            }
          }
        }
      }

      pushTraceSelectionUndoEntry({
        kind: "reset_overrides",
        label: `Reset per-channel overrides (${allMontageChannelLabels.length})`,
        channels: allMontageChannelLabels,
        previousChannelGainOverrides,
        previousHighlightByChannel,
        previousProfileChannelPositionOffsets,
        previousProfileChannelColors,
        previousFilterByChannel,
      });
      setSelectedTraceActionStatus(
        `Reset per-channel overrides for ${allMontageChannelLabels.length} channel(s).`,
      );
      await queryClient.invalidateQueries({
        queryKey: filterOverridesQueryKey,
      });
    } catch (error) {
      queryClient.setQueryData<FilterOverride[]>(
        filterOverridesQueryKey,
        previousFilterRows,
      );
      setChannelGainOverrides(previousChannelGainOverrides);
      setTemporaryTraceHighlights(previousHighlightByChannel);
      setProfileChannelPositionOffsets(previousProfileChannelPositionOffsets);
      setProfileChannelColors(previousProfileChannelColors);
      setSelectedTraceActionError(toUserFacingApiError(error).message);
    } finally {
      setIsApplyingSelectedTraceAction(false);
    }
  }, [
    allMontageChannelLabels,
    channelGainOverrides,
    clinicalScopeId,
    dialog,
    filterOverrideRows,
    filterOverridesQueryKey,
    profileChannelColors,
    profileChannelPositionOffsets,
    pushTraceSelectionUndoEntry,
    queryClient,
    setProfileChannelColors,
    setProfileChannelPositionOffsets,
    setTemporaryTraceHighlights,
    temporaryTraceHighlights,
  ]);

  const parseTraceFilterValues = useCallback(() => {
    const hp =
      traceFilterHp.trim() === ""
        ? null
        : Number.parseFloat(traceFilterHp.trim());
    const lp =
      traceFilterLp.trim() === ""
        ? null
        : Number.parseFloat(traceFilterLp.trim());
    const notch =
      traceFilterNotch.trim() === ""
        ? null
        : Number.parseFloat(traceFilterNotch.trim());

    if (hp !== null && (!Number.isFinite(hp) || hp < 0)) {
      setSelectedTraceActionError("HP must be a non-negative number.");
      return null;
    }
    if (lp !== null && (!Number.isFinite(lp) || lp <= 0)) {
      setSelectedTraceActionError("LP must be a positive number.");
      return null;
    }
    if (hp !== null && lp !== null && hp >= lp) {
      setSelectedTraceActionError("HP must be lower than LP.");
      return null;
    }
    if (notch !== null && notch !== 50 && notch !== 60) {
      setSelectedTraceActionError("Notch must be 50 or 60.");
      return null;
    }
    if (hp === null && lp === null && notch === null) {
      setSelectedTraceActionError(
        "Enter at least one filter value (HP, LP, or notch).",
      );
      return null;
    }

    return { hp, lp, notch };
  }, [traceFilterHp, traceFilterLp, traceFilterNotch]);

  const handleMarkSelectedTraceChannelsBad = useCallback(async () => {
    if (!clinicalScopeId) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No active recording selected.");
      return;
    }
    if (selectedTraceActionTargets.length === 0) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Select at least one trace channel first.");
      return;
    }

    setIsApplyingSelectedTraceAction(true);
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(null);

    const previousRows =
      queryClient.getQueryData<BadChannelMark[]>(badChannelsQueryKey) ||
      badChannelRows;
    const previousBadByChannel: Record<string, BadChannelMark | null> = {};
    selectedTraceActionTargets.forEach((channel) => {
      const normalized = normalizeChannelPairKey(channel);
      previousBadByChannel[channel] =
        previousRows.find(
          (row) => normalizeChannelPairKey(row.channel_name) === normalized,
        ) || null;
    });

    const nowIso = new Date().toISOString();
    const optimisticRows = [...previousRows];
    selectedTraceActionTargets.forEach((channel, index) => {
      if (previousBadByChannel[channel]) return;
      optimisticRows.push({
        id: `optimistic-bad-${Date.now()}-${index}`,
        channel_name: channel,
        reason_code: "manual_override",
        note: "Marked from EEGViewer selected-trace actions.",
        marked_at: nowIso,
      });
    });
    queryClient.setQueryData<BadChannelMark[]>(
      badChannelsQueryKey,
      optimisticRows,
    );

    try {
      await Promise.all(
        selectedTraceActionTargets.map(async (channel) => {
          if (previousBadByChannel[channel]) return;
          try {
            await apiClient.post(
              `/eeg/recordings/${clinicalScopeId}/bad-channels`,
              {
                channel_name: channel,
                reason_code: "manual_override",
                note: "Marked from EEGViewer selected-trace actions.",
              },
            );
          } catch (error) {
            const normalized = toUserFacingApiError(error);
            if (normalized.status === 409) return;
            throw error;
          }
        }),
      );

      pushTraceSelectionUndoEntry({
        kind: "mark_bad",
        label: `Mark bad (${selectedTraceActionTargets.length})`,
        channels: selectedTraceActionTargets,
        previousBadByChannel,
      });
      setSelectedTraceActionStatus(
        `Marked ${selectedTraceActionTargets.length} channel(s) as bad.`,
      );
      await queryClient.invalidateQueries({ queryKey: badChannelsQueryKey });
    } catch (error) {
      queryClient.setQueryData<BadChannelMark[]>(
        badChannelsQueryKey,
        previousRows,
      );
      setSelectedTraceActionError(toUserFacingApiError(error).message);
    } finally {
      setIsApplyingSelectedTraceAction(false);
    }
  }, [
    badChannelRows,
    badChannelsQueryKey,
    clinicalScopeId,
    pushTraceSelectionUndoEntry,
    queryClient,
    selectedTraceActionTargets,
  ]);

  const handleApplyFilterOverrideToSelectedTraces = useCallback(async () => {
    if (!clinicalScopeId) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No active recording selected.");
      return;
    }
    if (selectedTraceActionTargets.length === 0) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Select at least one trace channel first.");
      return;
    }

    const parsed = parseTraceFilterValues();
    if (!parsed) return;

    setIsApplyingSelectedTraceAction(true);
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(null);

    const previousRows =
      queryClient.getQueryData<FilterOverride[]>(filterOverridesQueryKey) ||
      filterOverrideRows;
    const previousFilterByChannel: Record<string, FilterOverride | null> = {};
    selectedTraceActionTargets.forEach((channel) => {
      const normalized = normalizeChannelPairKey(channel);
      previousFilterByChannel[channel] =
        previousRows.find(
          (row) => normalizeChannelPairKey(row.channel_name) === normalized,
        ) || null;
    });

    const nowIso = new Date().toISOString();
    const optimisticByNormalized = new Map(
      previousRows.map((row) => [
        normalizeChannelPairKey(row.channel_name),
        row,
      ]),
    );
    selectedTraceActionTargets.forEach((channel, index) => {
      const normalized = normalizeChannelPairKey(channel);
      optimisticByNormalized.set(normalized, {
        id:
          previousFilterByChannel[channel]?.id ||
          `optimistic-filter-${Date.now()}-${index}`,
        channel_name: channel,
        hp: parsed.hp,
        lp: parsed.lp,
        notch: parsed.notch,
        updated_at: nowIso,
      });
    });
    queryClient.setQueryData<FilterOverride[]>(
      filterOverridesQueryKey,
      Array.from(optimisticByNormalized.values()),
    );

    try {
      await Promise.all(
        selectedTraceActionTargets.map(async (channel) => {
          await apiClient.put(
            `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
            parsed,
          );
        }),
      );

      pushTraceSelectionUndoEntry({
        kind: "apply_filter",
        label: `Apply filter (${selectedTraceActionTargets.length})`,
        channels: selectedTraceActionTargets,
        previousFilterByChannel,
      });
      setSelectedTraceActionStatus(
        `Applied filter override to ${selectedTraceActionTargets.length} channel(s).`,
      );
      await queryClient.invalidateQueries({
        queryKey: filterOverridesQueryKey,
      });
    } catch (error) {
      queryClient.setQueryData<FilterOverride[]>(
        filterOverridesQueryKey,
        previousRows,
      );
      setSelectedTraceActionError(toUserFacingApiError(error).message);
    } finally {
      setIsApplyingSelectedTraceAction(false);
    }
  }, [
    filterOverrideRows,
    filterOverridesQueryKey,
    clinicalScopeId,
    parseTraceFilterValues,
    pushTraceSelectionUndoEntry,
    queryClient,
    selectedTraceActionTargets,
  ]);

  const handleRemoveFilterOverrideFromSelectedTraces = useCallback(async () => {
    if (!clinicalScopeId) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("No active recording selected.");
      return;
    }
    if (selectedTraceActionTargets.length === 0) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Select at least one trace channel first.");
      return;
    }

    setIsApplyingSelectedTraceAction(true);
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(null);

    const previousRows =
      queryClient.getQueryData<FilterOverride[]>(filterOverridesQueryKey) ||
      filterOverrideRows;
    const previousFilterByChannel: Record<string, FilterOverride | null> = {};
    selectedTraceActionTargets.forEach((channel) => {
      const normalized = normalizeChannelPairKey(channel);
      previousFilterByChannel[channel] =
        previousRows.find(
          (row) => normalizeChannelPairKey(row.channel_name) === normalized,
        ) || null;
    });

    const optimisticRows = previousRows.filter((row) => {
      const normalized = normalizeChannelPairKey(row.channel_name);
      return !selectedTraceActionTargets.some(
        (channel) => normalizeChannelPairKey(channel) === normalized,
      );
    });
    queryClient.setQueryData<FilterOverride[]>(
      filterOverridesQueryKey,
      optimisticRows,
    );

    try {
      await Promise.all(
        selectedTraceActionTargets.map(async (channel) => {
          if (!previousFilterByChannel[channel]) return;
          try {
            await apiClient.delete(
              `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
            );
          } catch (error) {
            const normalized = toUserFacingApiError(error);
            if (normalized.status === 404) return;
            throw error;
          }
        }),
      );

      pushTraceSelectionUndoEntry({
        kind: "remove_filter",
        label: `Remove filter (${selectedTraceActionTargets.length})`,
        channels: selectedTraceActionTargets,
        previousFilterByChannel,
      });
      setSelectedTraceActionStatus(
        `Removed filter override from ${selectedTraceActionTargets.length} channel(s).`,
      );
      await queryClient.invalidateQueries({
        queryKey: filterOverridesQueryKey,
      });
    } catch (error) {
      queryClient.setQueryData<FilterOverride[]>(
        filterOverridesQueryKey,
        previousRows,
      );
      setSelectedTraceActionError(toUserFacingApiError(error).message);
    } finally {
      setIsApplyingSelectedTraceAction(false);
    }
  }, [
    filterOverrideRows,
    filterOverridesQueryKey,
    clinicalScopeId,
    pushTraceSelectionUndoEntry,
    queryClient,
    selectedTraceActionTargets,
  ]);

  const applyTemporaryHighlightToSelectedTraces = useCallback(() => {
    if (selectedTraceActionTargets.length === 0) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Select at least one trace channel first.");
      return;
    }
    const normalizedColor = normalizeHexColor(traceHighlightColor);
    if (!normalizedColor) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Choose a valid highlight color.");
      return;
    }

    const previousHighlightByChannel: Record<string, string | null> = {};
    selectedTraceActionTargets.forEach((channel) => {
      const normalizedKey = normalizeChannelPairKey(channel);
      previousHighlightByChannel[channel] =
        temporaryTraceHighlights[normalizedKey] || null;
    });

    setTemporaryTraceHighlights((current) => {
      const next = { ...current };
      selectedTraceActionTargets.forEach((channel) => {
        next[normalizeChannelPairKey(channel)] = normalizedColor;
      });
      return next;
    });

    pushTraceSelectionUndoEntry({
      kind: "temp_highlight",
      label: `Highlight traces (${selectedTraceActionTargets.length})`,
      channels: selectedTraceActionTargets,
      previousHighlightByChannel,
    });
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(
      `Applied temporary highlight to ${selectedTraceActionTargets.length} channel(s).`,
    );
  }, [
    pushTraceSelectionUndoEntry,
    selectedTraceActionTargets,
    setTemporaryTraceHighlights,
    temporaryTraceHighlights,
    traceHighlightColor,
  ]);

  const clearTemporaryHighlightFromSelectedTraces = useCallback(() => {
    if (selectedTraceActionTargets.length === 0) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Select at least one trace channel first.");
      return;
    }

    const previousHighlightByChannel: Record<string, string | null> = {};
    selectedTraceActionTargets.forEach((channel) => {
      const normalizedKey = normalizeChannelPairKey(channel);
      previousHighlightByChannel[channel] =
        temporaryTraceHighlights[normalizedKey] || null;
    });

    setTemporaryTraceHighlights((current) => {
      const next = { ...current };
      selectedTraceActionTargets.forEach((channel) => {
        delete next[normalizeChannelPairKey(channel)];
      });
      return next;
    });

    pushTraceSelectionUndoEntry({
      kind: "temp_highlight",
      label: `Clear highlight (${selectedTraceActionTargets.length})`,
      channels: selectedTraceActionTargets,
      previousHighlightByChannel,
    });
    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(
      `Cleared temporary highlight from ${selectedTraceActionTargets.length} channel(s).`,
    );
  }, [
    pushTraceSelectionUndoEntry,
    selectedTraceActionTargets,
    setTemporaryTraceHighlights,
    temporaryTraceHighlights,
  ]);

  const handleUndoLastSelectedTraceAction = useCallback(async () => {
    const lastEntry =
      traceSelectionUndoStack[traceSelectionUndoStack.length - 1];
    if (!lastEntry) {
      setSelectedTraceActionStatus(null);
      setSelectedTraceActionError("Nothing to undo.");
      return;
    }

    setSelectedTraceActionError(null);
    setSelectedTraceActionStatus(null);
    setIsApplyingSelectedTraceAction(true);

    try {
      if (lastEntry.kind === "selection_bulk") {
        setSelectedTraceChannels(lastEntry.previousSelection);
        lastTraceSelectionRef.current =
          lastEntry.previousSelection.length > 0
            ? lastEntry.previousSelection[
                lastEntry.previousSelection.length - 1
              ]
            : null;
      } else if (lastEntry.kind === "temp_highlight") {
        setTemporaryTraceHighlights((current) => {
          const next = { ...current };
          lastEntry.channels.forEach((channel) => {
            const normalized = normalizeChannelPairKey(channel);
            const previousColor = lastEntry.previousHighlightByChannel[channel];
            if (previousColor) {
              const normalizedColor = normalizeHexColor(previousColor);
              if (normalizedColor) {
                next[normalized] = normalizedColor;
                return;
              }
            }
            delete next[normalized];
          });
          return next;
        });
      } else if (lastEntry.kind === "reset_overrides") {
        setChannelGainOverrides(lastEntry.previousChannelGainOverrides);
        setTemporaryTraceHighlights(lastEntry.previousHighlightByChannel);
        setProfileChannelPositionOffsets(
          lastEntry.previousProfileChannelPositionOffsets,
        );
        setProfileChannelColors(lastEntry.previousProfileChannelColors);

        const hasFilterRestore = Object.values(
          lastEntry.previousFilterByChannel,
        ).some(Boolean);
        if (hasFilterRestore) {
          if (!clinicalScopeId) {
            throw new Error("No active recording selected.");
          }
          for (const channel of lastEntry.channels) {
            const previous = lastEntry.previousFilterByChannel[channel];
            if (previous) {
              await apiClient.put(
                `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
                {
                  hp: previous.hp,
                  lp: previous.lp,
                  notch: previous.notch,
                },
              );
              continue;
            }
            try {
              await apiClient.delete(
                `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
              );
            } catch (error) {
              const normalized = toUserFacingApiError(error);
              if (normalized.status !== 404) {
                throw error;
              }
            }
          }
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: filterOverridesQueryKey,
            }),
            refetchFilterOverrides(),
          ]);
        }
      } else if (lastEntry.kind === "mark_bad") {
        if (!clinicalScopeId) {
          throw new Error("No active recording selected.");
        }
        for (const channel of lastEntry.channels) {
          const previous = lastEntry.previousBadByChannel[channel];
          if (previous) continue;
          try {
            await apiClient.delete(
              `/eeg/recordings/${clinicalScopeId}/bad-channels/${encodeURIComponent(channel)}`,
            );
          } catch (error) {
            const normalized = toUserFacingApiError(error);
            if (normalized.status !== 404) {
              throw error;
            }
          }
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: badChannelsQueryKey }),
          refetchBadChannels(),
        ]);
      } else if (
        lastEntry.kind === "apply_filter" ||
        lastEntry.kind === "remove_filter"
      ) {
        if (!clinicalScopeId) {
          throw new Error("No active recording selected.");
        }
        for (const channel of lastEntry.channels) {
          const previous = lastEntry.previousFilterByChannel[channel];
          if (previous) {
            await apiClient.put(
              `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
              {
                hp: previous.hp,
                lp: previous.lp,
                notch: previous.notch,
              },
            );
            continue;
          }

          try {
            await apiClient.delete(
              `/eeg/recordings/${clinicalScopeId}/channel-filters/${encodeURIComponent(channel)}`,
            );
          } catch (error) {
            const normalized = toUserFacingApiError(error);
            if (normalized.status !== 404) {
              throw error;
            }
          }
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: filterOverridesQueryKey }),
          refetchFilterOverrides(),
        ]);
      }

      setTraceSelectionUndoStack((current) => current.slice(0, -1));
      setSelectedTraceActionStatus(`Undid: ${lastEntry.label}.`);
    } catch (error) {
      setSelectedTraceActionError(toUserFacingApiError(error).message);
    } finally {
      setIsApplyingSelectedTraceAction(false);
    }
  }, [
    badChannelsQueryKey,
    clinicalScopeId,
    filterOverridesQueryKey,
    queryClient,
    refetchBadChannels,
    refetchFilterOverrides,
    setProfileChannelColors,
    setProfileChannelPositionOffsets,
    setTemporaryTraceHighlights,
    traceSelectionUndoStack,
  ]);

  return {
    // State
    selectedTraceChannels,
    setSelectedTraceChannels,
    lockedTraceFocusLabel,
    setLockedTraceFocusLabel,
    channelRegionSelection,
    setChannelRegionSelection,
    channelQuickJumpInput,
    setChannelQuickJumpInput,
    traceFilterHp,
    setTraceFilterHp,
    traceFilterLp,
    setTraceFilterLp,
    traceFilterNotch,
    setTraceFilterNotch,
    traceHighlightColor,
    setTraceHighlightColor,
       selectedTraceActionStatus,
       setSelectedTraceActionStatus,
       selectedTraceActionError,
       setSelectedTraceActionError,
       isApplyingSelectedTraceAction,
    traceSelectionUndoStack,
    channelScrollOffset,
    setChannelScrollOffset,
    visibleChannelCount,
    setVisibleChannelCount,
    channelGainOverrides,
    setChannelGainOverrides,
    channelGainMenu,
    setChannelGainMenu,
    expandedSpacingChannelLabel,
    setExpandedSpacingChannelLabel,

    // Refs
    channelGainMenuRef,
    lastTraceSelectionRef,

    // Derived
    selectedTraceActionTargets,
    maxChannelScrollOffset,

    // Phase 1 handlers
    handleWaveformCanvasChannelSelection,
    estimateChannelAutoScaleGain,
    hideChannel,
    isolateChannel,
    showAllChannels,
    isolateChannels,
    applyChannelGainOverride,
    closeChannelGainMenu,
    toggleExpandedSpacingForLabel,
    handleChannelGainPresetSelect,
    handleChannelAutoScaleSelect,
    handleChannelHideSelect,
    handleChannelIsolateSelect,
    handleChannelShowAllSelect,
    handleChannelExpandedSpacingSelect,
    handleResetExpandedSpacingSelect,
    handleToggleTraceFocusLockSelect,
    clearTraceFocusLock,
    handleSelectedChannelSpacingToggle,
    handleResetAllGainsSelect,
    handleResetChannelAppearanceSelect,
    handleResetChannelGainSelect,

    // Phase 2 handlers (bulk selection / undo)
    pushTraceSelectionUndoEntry,
    applyBulkTraceSelection,
    handleSelectAllTraceChannels,
    handleClearAllTraceChannels,
    handleSelectVisibleTraceChannels,
    handleInvertTraceChannelSelection,
    handleSelectTraceChannelsByRegion,
    handleQuickJumpToChannelLabel,
    handleResetAllPerChannelOverrides,
    parseTraceFilterValues,
    handleMarkSelectedTraceChannelsBad,
    handleApplyFilterOverrideToSelectedTraces,
    handleRemoveFilterOverrideFromSelectedTraces,
    applyTemporaryHighlightToSelectedTraces,
    clearTemporaryHighlightFromSelectedTraces,
    handleUndoLastSelectedTraceAction,
  };
}
